import { createSelector, Dictionary } from '@reduxjs/toolkit'
import searchSlice, { SearchState } from 'app/search/searchSlice'
import { RootState } from 'app/store'
import { ProductsResultFragmentDoc } from 'app/services/api'
import { PRODUCT_URI_FRONT } from 'config'
import {
  asArray,
  asTaxonomyEnum,
  getSelectionsTermTree,
  getTermAncestors,
  getTermTreeLeaves,
  isNullish,
  isTaxonomyState,
  maybeArrayToString,
  trimSlashes,
} from 'helpers'
import {
  ProductSearchArgs,
  QueriedObject,
  QueriedObjectType,
  TaxonomyId,
  TaxonomyState,
  TaxQueryNode,
  Term,
  TermId,
  TermIdentifier,
  TermList,
  TermSelectPayload,
} from 'types'
import { selectPagination, selectProductBySlug } from './results/resultsSlice'
import settingsSlice, { SettingsState } from './settings/settingsSlice'
import { useAppDispatch, useAppSelector } from 'app/hooks'

// Slice selectors
export const stateSelector = (state: RootState): RootState => state
export const searchSelector = (state: RootState): SearchState => state[searchSlice.name]
export const settingsSelector = (state: RootState): SettingsState => state[settingsSlice.name]

// Basic values
export const selectIncludedTaxonomies = createSelector(settingsSelector, (state) => state.includedTaxonomies)
export const selectIncludedCountries = createSelector(settingsSelector, (state) => state.includedCountries)
export const selectSettings = createSelector(settingsSelector, (state) => state.includedCountries)
export const selectSettingsLoaded = createSelector(settingsSelector, (state) => state.settingsLoaded)
export const selectBaseUri = createSelector(settingsSelector, (state) => trimSlashes(`/${state.baseUri}`))

export const getSelectBaseUri =
  (uri: string = '') =>
  (state: RootState) => {
    return `/${trimSlashes(settingsSelector(state).baseUri)}/${trimSlashes(uri)}`
  }

export const getSelectProductUri = (slugId: string | number) => {
  return getSelectBaseUri(`${trimSlashes(PRODUCT_URI_FRONT)}/${trimSlashes(`${slugId}`)}`)
}

export const getSelectTermSlug = (taxonomyId: TaxonomyId, id: TermId) => (state: RootState) => {
  const term = state[taxonomyId].entities[id]
  return term ? term.uri : ''
}

export const getProductSlugByUri = (currentUri?: string | null) => (state: RootState) => {
  if (!currentUri) return null
  const trimmedBaseUri = trimSlashes(getSelectBaseUri('')(state))
  const trimmedUri = trimSlashes(currentUri)

  if (trimmedUri.startsWith(trimmedBaseUri)) {
    const slug = trimmedUri.substring(trimmedBaseUri.length + 1)
    if (slug) return slug
  }

  return null
}

/**
 * Record listing taxonomy uris by `TaxonomyId`
 */
export const selectTaxonomyUris = createSelector(settingsSelector, (settings) => {
  return settings.taxonomySlugs
})

export const selectTermByUri = createSelector(
  stateSelector,
  selectTaxonomyUris,
  (state: RootState, taxonomyUris): TermSelectPayload | null => {
    const currentUri = state.search.currentUri

    //Bail early
    if (!currentUri) return null

    /**
     * Only parse the routes for these taxonomies
     */
    const routeTaxonomies: TaxonomyId[] = ['BRAND', 'PRODUCTCATEGORY', 'PRODUCINGCOUNTRY']

    /**
     * Find the active taxonomy, if any, based on the current URI
     */
    const activeTaxonomyId: TaxonomyId | undefined = (Object.keys(taxonomyUris) as (keyof typeof taxonomyUris)[]).find((taxonomyId) => {
      const taxUri = routeTaxonomies.includes(taxonomyId) ? taxonomyUris[taxonomyId] : null
      return taxUri && currentUri.includes(trimSlashes(taxUri))
    })

    if (!activeTaxonomyId) return null
    const activeTaxonomy = state[activeTaxonomyId]

    if (!activeTaxonomy) return null
    const { entities: terms } = activeTaxonomy

    let termId: keyof typeof terms | undefined = Object.values(terms).find(({ uri }) => {
      return uri === currentUri
    })?.id

    termId = !isNaN(Number(termId)) ? Number(termId) : undefined

    return termId
      ? {
          taxonomyId: activeTaxonomyId,
          termId,
          checked: true,
        }
      : null
  }
)

export const selectQueriedObject = createSelector(
  stateSelector,
  searchSelector,
  selectTermByUri,
  (state, searchState, selectedTerm): QueriedObject | null => {
    const { currentUri } = searchState

    if (selectedTerm) {
      return {
        type: QueriedObjectType.TERM,
        id: {
          taxonomyId: selectedTerm.taxonomyId,
          id: selectedTerm.termId,
        },
      }
    }

    const productSlug = getProductSlugByUri(currentUri)(state)

    if (productSlug) {
      return {
        type: QueriedObjectType.PRODUCT,
        id: productSlug,
      }
    }

    return null
  }
)

export const selectQueriedTerm = createSelector(selectQueriedObject, (object): TermIdentifier | null => {
  return object && object.type === QueriedObjectType.TERM ? object.id : null
})

export type Breadcrumbs = [label: string, uri: string | undefined][]

export const selectBreadcrumbs = createSelector(stateSelector, selectQueriedObject, (state, queriedObject): Breadcrumbs => {
  const breadcrumbs: Breadcrumbs = [['Product Finder', getSelectBaseUri('')(state)]]

  if (!queriedObject) return breadcrumbs

  if (queriedObject.type === QueriedObjectType.PRODUCT) {
    const product = selectProductBySlug(queriedObject.id)(state)
    if (product?.title) {
      breadcrumbs.push(['Product', getSelectBaseUri('')(state)]);
      const productCategory = product.productCategories.length ? product.productCategories[0] : undefined
      if (productCategory) {
        const { taxonomyId, id } = productCategory;
        const theProductCategory = getSelectTermById(taxonomyId, id)(state)
        if (theProductCategory) breadcrumbs.push([theProductCategory.name, theProductCategory.uri])
      }
      const productBrand = product.brands.length ? product.brands[0] : undefined
      if (productBrand) {
        const { taxonomyId, id } = productBrand;
        const theProductBrand = getSelectTermById(taxonomyId, id)(state)
        if (theProductBrand) breadcrumbs.push([theProductBrand.name, theProductBrand.uri])
      }
      breadcrumbs.push([product.title, undefined])
    }
  } else {
    const { id: termId, taxonomyId } = queriedObject.id
    const taxonomyState = state[taxonomyId]
    const termWithAncestors = getTermAncestors(termId, taxonomyState.entities)
    const terms = getSelectTermsById(taxonomyId, termWithAncestors)(state).sort((a, b) => (`${a?.uri}` < `${b?.uri}` ? -1 : 1))

    terms.forEach((t) => {
      if (t) breadcrumbs.push([t.name, t.uri])
    })
  }

  return breadcrumbs
})

export const selectGlobalCopy = createSelector(settingsSelector, (state) => state.globalCopy)

export const selectPacificSticker = createSelector(settingsSelector, (state) => state.pacificSticker)

export const selectPngSticker = createSelector(settingsSelector, (state) => state.pngSticker)

/**
 * Placeholder selector used in 'useSelector' hooks where we don't actually want a result yet.
 */
export const noSelector =
  <T = undefined>(args?: T) =>
  (state: RootState): T | undefined =>
    args

/**
 * The search query string
 */
export const selectSearchQuery = createSelector(searchSelector, (state) => state.search)


/**
 * The search type
 */
export const selectSearchType = createSelector(searchSelector, (state) => state.type)

/**
 * Select all taxonomy slices, where TaxonomyId
 * is in `includedTaxonomies` from {@link settingsSelector} - defined in the CMS
 */
export const taxSelector = (state: RootState) => {
  const { includedTaxonomies } = settingsSelector(state)

  return Object.values(state).filter((sliceState) => {
    return isTaxonomyState(sliceState) && includedTaxonomies.includes(sliceState.taxonomyId)
  }) as TaxonomyState<TaxonomyId>[]
}

export const selectTaxonomies = createSelector(taxSelector, selectIncludedTaxonomies, (taxonomyStates) => {
  return taxonomyStates
})

export const getSelectTermsById =
  <T extends TaxonomyId>(taxonomyId: T, ids: TermList) =>
  (state: RootState): (Term<T> | undefined)[] => {
    const tax = state[taxonomyId]
    const terms = tax.entities as Dictionary<Term<T>>
    return ids.map((id) => terms[id])
  }

export const getSelectTermById =
  <T extends TaxonomyId>(taxonomyId: T, id: TermId) =>
  (state: RootState): Term<T> | undefined => {
    const tax = state[taxonomyId]
    const terms = tax.entities as Dictionary<Term<T>>
    return terms[id]
  }

export const mainCategoriesSelector = (state: RootState): TaxonomyState<'PRODUCTCATEGORY'> => state.PRODUCTCATEGORY

export const selectMainCategories = createSelector(mainCategoriesSelector, (mainCategories) => {
  const { termTree: mainCategoryTermTree = [], entities: mainCategoryDict = {} } = mainCategories ?? {}
  return mainCategoryTermTree.reduce((results, { id }) => {
    const term = mainCategoryDict[id]
    return term ? results.concat(term) : results
  }, [] as Term[])
})

export const selectMainCategoryDefaultImage = (id: TermId) => (state: RootState) => {
  const cats = mainCategoriesSelector(state)
  const cat = cats.entities[id]
  return cat ? cat.defaultImage : undefined
}

export const selectIsFiltered = createSelector(taxSelector, selectSearchQuery, (taxonomyStates, searchQuery) => {
  if (searchQuery.length) return true
  return taxonomyStates.find((v) => !v.exclusive && v.selections.length > 0) ? true : false
})

export const selectTaxonomyQueries = createSelector(taxSelector, selectTaxonomies, (state, taxonomies): TaxQueryNode[] => {
  return taxonomies.reduce((nodes, taxonomyState) => {
    const { taxonomyId: taxonomy } = taxonomyState

    const terms = getTermTreeLeaves(getSelectionsTermTree(taxonomyState))

    if (!terms.length) return nodes

    const queryNode: TaxQueryNode = {
      taxonomy: asTaxonomyEnum(taxonomy),
      terms: terms.map((t) => `${t}`),
      includeChildren: true,
    }

    return nodes.concat(queryNode)
  }, [] as TaxQueryNode[])
})

/**
 * Collect the entire state and apply to produce a product search
 */
export const productSearchQuerySelector = (state: RootState): ProductSearchArgs => {
  const search = selectSearchQuery(state)
  const { offset, size } = selectPagination(state)
  const taxArray: TaxQueryNode[] = selectTaxonomyQueries(state)
  const type = selectSearchType(state)
  const brands = selectTaxonomies(state).filter((t) => t.taxonomyId === 'BRAND').map((t) => t.entities)

  return { search, offset, size, taxArray, type, brands }
}

export const productSearchQueryVariablesParser = (args: ProductSearchArgs): string => {
  const { taxArray = [], offset, size, notIn = [], type } = args
  const brands = args.brands && args.brands[0] ? args.brands[0] as Term[] : undefined;
  const search = args.search && type === 'products' ? args.search : '';

  // Example: [{taxonomy: PRODUCINGCOUNTRY, terms: ["73", "76"], includeChildren: true}]
  let taxArrayString = asArray(taxArray)
    .reduce((results, node) => {
      if (isNullish(node)) return results

      const { taxonomy, terms, includeChildren = true } = node

      if (isNullish(taxonomy)) return results

      const termsStr = asArray(terms)
        .map((t) => `"${t}"`)
        .join(', ')
      const includeChildrenStr = includeChildren ? 'true' : 'false'

      return results.concat(`{ taxonomy: ${taxonomy}, terms: [${termsStr}], includeChildren: ${includeChildrenStr} }`)
    }, [] as string[])
    .join(', ')

  if (type === 'brands' && brands) {
    const searchQuery = args.search?.toLowerCase() || ''
    const brandsDataObjectKeys = Object.keys(brands)
    const brandsDataObjectValues = Object.values(brands)

    const brandsDataObjectValuesWithKeys = brandsDataObjectValues.map((brand) => {
      const brandWithKeys = {
        ...brand,
        ...{
          "id": brandsDataObjectKeys.indexOf(brand.id.toString())
        }
      }
      return brandWithKeys
    })

    const brandsDataObjectValuesWithKeysFiltered = brandsDataObjectValuesWithKeys.filter((brand) => {
      return brand.slug.toLowerCase().includes(searchQuery)
    });

    const brandsTaxString = `{ taxonomy: BRAND, terms: [${brandsDataObjectValuesWithKeysFiltered.map((brand) => `"${brand.slug}"`).join(', ')}], operator: IN, field: SLUG, includeChildren: false }`;
    taxArrayString = taxArrayString ? `${taxArrayString}, ${brandsTaxString}` : brandsTaxString;
  }

  const paginationString = `offsetPagination: {offset: ${offset}, size: ${size}}`

  const query = `
    query products {
      products(where: { search: "${search}", notIn: ${maybeArrayToString(
    notIn,
    true
  )} ${paginationString}, taxQuery: { taxArray: [${taxArrayString}] } }) {
        ... ProductsResult
      }
    }
    ${ProductsResultFragmentDoc}`

  return query
}

export const selectProductSearchQuery = createSelector(productSearchQuerySelector, (searchArgs) => searchArgs)

export const relatedProductsSearchQuery = (
  productId: number,
  term: Pick<Term, 'id' | 'taxonomyId'>,
  size: number = 3
): ProductSearchArgs => {
  return {
    search: '',
    size,
    offset: 0,
    notIn: [`${productId}`],
    taxArray: [{ taxonomy: asTaxonomyEnum(term.taxonomyId), terms: [`${term.id}`] }],
    type: 'products',
  }
}

